import React, { Component } from 'react';
import {
    SofaFormControl,
    SofaFormValidator,
} from '@sofology/sofologycomponents';

import './DeliveryAddressForm.scss';

import { AddressModel, IfcAddressDescription, PostcodeModel } from 'models';
import { BasketErrorModel, BasketHybrisFormErrorModel } from 'models/Errors';
import {
    POSTCODE_INVALID_ERROR_MESSAGE,
    POSTCODE_IS_EIRE_FOR_IFC_ERROR_MESSAGE
} from '../deliveryDetailsAdditionalData';
import { generateRandomId, paymentHelper } from 'helpers';
import { PaymentTypes } from 'models/Payment';
import { SofaFormComponent } from 'interfaces';
import AddressEditor, { AddressEditorModes, AddressEditorType } from 'components/AddressEditor/AddressEditor';

const START_DELIVERY_ADDRESS_MANUAL_FIELD_INDEX = 8;
const GTM_FORM_NAME = 'deliveryDetails';

interface OwnProps {
    defaultAddress: AddressModel | null;
    ref?: React.RefObject<DeliveryAddressFormType>;
    deliveryDetailsUpdatingErrors: BasketErrorModel[] | null;
    paymentType: PaymentTypes;
    onAddressChanged: (address: AddressModel | null) => void;
    ifcAddressQuestionSet: IfcAddressDescription;
    fixedPostcode: string;
}

interface OwnState {
    addressFormId: string;
    defaultAddress: AddressModel | null;
}

class DeliveryAddressForm extends Component<OwnProps, OwnState> implements SofaFormComponent<AddressModel> {
    private addressEditor = React.createRef<AddressEditorType>();

    constructor(props: OwnProps) {
        super(props);

        this.state = {
            addressFormId: generateRandomId(),
            defaultAddress: this.props.defaultAddress,
        };
    }

    handleAddressChanged = (addressComponentMode: AddressEditorModes, address: AddressModel | null) => {
        this.props.onAddressChanged(address);
    };

    render(): JSX.Element {
        const { addressFormId, defaultAddress } = this.state;
        const { ifcAddressQuestionSet, fixedPostcode } = this.props;

        return (
            <div className='delivery-address-form'>
                <AddressEditor
                    startControlIndex={START_DELIVERY_ADDRESS_MANUAL_FIELD_INDEX + 1}
                    defaultAddress={defaultAddress}
                    ifcAddressQuestionSet={ifcAddressQuestionSet}
                    gtmFormName={GTM_FORM_NAME}
                    addressFormId={addressFormId}
                    ref={this.addressEditor}
                    additionalValidators={{
                        postcode: [
                            this.postcodeEirValidator(POSTCODE_IS_EIRE_FOR_IFC_ERROR_MESSAGE),
                            this.postcodeServerValidator(POSTCODE_INVALID_ERROR_MESSAGE)
                        ]
                    }}
                    onChange={this.handleAddressChanged}
                    fixedPostcode={fixedPostcode}
                />
            </div>
        );
    }

    public validate = (): Promise<SofaFormControl<any>[]> => {
        return this.addressEditor.current!.validate();
    };

    public getFormData = (): AddressModel => {
        return this.addressEditor.current!.getFormData();
    };

    postcodeServerValidator = (errorMessage: string): SofaFormValidator<string> => {
        return (value: string, isChangedAfterExternalValidation: boolean) => {
            let isInvalidPostcode = Boolean(
                !isChangedAfterExternalValidation
                && this.props.deliveryDetailsUpdatingErrors
                && this.props.deliveryDetailsUpdatingErrors.find(e =>
                    e instanceof BasketHybrisFormErrorModel
                    && e.subject === 'postalCode'
                    && e.type === 'ValidationError'
                    && e.reason === 'invalid'
                    && e.formName === 'deliveryDetails'));

            return Promise.resolve(isInvalidPostcode ? errorMessage : null);
        };
    };

    postcodeEirValidator = (errorMessage: string): SofaFormValidator<string> => {
        return (value: string) => {
            let isInvalidPostcode = Boolean(
                paymentHelper.isCreditRelatedPaymentType(this.props.paymentType)
                && value.length > 0
                && (new PostcodeModel(value)).isEirCode);

            return Promise.resolve(isInvalidPostcode ? errorMessage : null);
        };
    };
}

export default DeliveryAddressForm;
export type DeliveryAddressFormType = DeliveryAddressForm;